/* eslint no-unused-expressions: 0, func-names: 0 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
//import ReactGA from 'react-ga';
import Tooltip from './Tooltip.js';
import {
    COLORS,
    COMMUNITIES_TO_NAMES,
    CONGRESS_COLORS,
    IDS_TO_COMMUNITIES,
    MAPBOX_KEY,
    US_STATES_BOUNDS,
} from '../constants';
import analyticsMap from '../analytics-map';
import { getZoom, getPanDistance, getPadding } from '../libs/mapMethods';

mapboxgl.accessToken = MAPBOX_KEY;

// 2022 Congress Mapbox style: mapbox://styles/jacobhanson/cl2as09eq000815o5nzhaghj9
// 2022 Congress Mapbox style: mapbox://styles/jacobhanson/clo5vrxyy00i601qsbxcn5jja
const fullMap = 'mapbox://styles/jacobhanson/clo5vrxyy00i601qsbxcn5jja?fresh=true';

let map = {};

let mapOnLoad = false;
let styleLoaded = false;

const isMobile = window.innerWidth < 768;
const isMobileWide = window.innerWidth >= 570 && window.innerWidth < 768;
const numOfCategories = Object.keys(IDS_TO_COMMUNITIES).length;

const blankResultsList = {};
Object.keys(IDS_TO_COMMUNITIES).forEach(key => {
    blankResultsList[IDS_TO_COMMUNITIES[key]] = [];
});

// the district boundaries use FIP numbers for each State. so we need to convert state to FIP to show the proper
// boundary around the district
const fipStateData = {
    AL: '01',
    AK: '02',
    AZ: '04',
    AR: '05',
    CA: '06',
    CO: '08',
    CT: '09',
    DE: '10',
    DC: '11',
    FL: '12',
    GA: '13',
    HI: '15',
    ID: '16',
    IL: '17',
    IN: '18',
    IA: '19',
    KS: '20',
    KY: '21',
    LA: '22',
    ME: '23',
    MD: '24',
    MA: '25',
    MI: '26',
    MN: '27',
    MS: '28',
    MO: '29',
    MT: '30',
    NE: '31',
    NV: '32',
    NH: '33',
    NJ: '34',
    NM: '35',
    NY: '36',
    NC: '37',
    ND: '38',
    OH: '39',
    OK: '40',
    OR: '41',
    PA: '42',
    PR: '72',
    RI: '44',
    SC: '45',
    SD: '46',
    TN: '47',
    TX: '48',
    UT: '49',
    VT: '50',
    VA: '51',
    WA: '53',
    WV: '54',
    WI: '55',
    WY: '56',
};

const categoriesArray = [
    'organizations-arts',
    'organizations-education',
    'organizations-housing',
    'organizations-financial-education',
    'organizations-social-services',
    'organizations-civic-engagement',
    'organizations-small-business',
    'organizations-job-training',
    'organizations-environment',
];

let tagQueue = [];

const fireEvents = name => {
    tagQueue.push(name);
    setTimeout(() => {
        if (tagQueue.length > 0) {
            /*
            ReactGA.event({
                category: analyticsMap.G1.Category,
                action: tagQueue[0],
                label: window.location.href,
            });
            */
        }
        tagQueue = [];
    }, 300);
};

class CongressMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            markers: [],
        };
        this.tooltipClickContainer = document.createElement('div');
        this.tooltipClickContainer.id = 'cd-mapbox-pin';
    }

	// eslint-disable-next-line class-methods-use-this
    stateToFIP(StateAndDistrict) {
        if (StateAndDistrict === '') return '';

        const usState = StateAndDistrict.substring(0, 2);
        return fipStateData[usState] + StateAndDistrict.substring(2, 4);
    }

    async setIt(dontReCenterOnMapDrag) {
        // const propZoom = this.props.congress.mapbox.zoom;
        // const propCenter = this.props.congress.mapbox.center;
        //
        const { zoom: propZoom, center: propCenter } = this.props.congress.mapbox;

        // console.log({propZoom}, {propCenter});

        //////////////////////////////////////////////////////////////////////
        // function that FILTERS the mapbox organization pins for each category
        // based upon state or district drop down selections
        // or user provided search terms
        //////////////////////////////////////////////////////////////////////

        const showPins = async (filterArray) => {

          // filterArray format:
          // [ {'featureProperty': 'val', 'criteria': variable, 'type': 'val'} ]
          // example:
          // [ {'featureProperty': 'state', 'criteria': someVal, 'type': '=='},
          //   {'featureProperty': 'name', 'criteria': someOtherVal, 'type':'in' } ]

          //remove any open FEATURED popups
          this.clearElems('.mapboxgl-popup');

          // remove any open map pin popups.
          ReactDOM.unmountComponentAtNode(this.tooltipClickContainer);

          let filterInstructions = ["all"]; // tells Mapbox ALL criteria must match (equivalent to an "AND" condition)

          // passed in object gets transformed into query syntax for Mapbox filtering
          // loop through filterArray items and insert proper syntax into filterInstructions
          if (filterArray.length) {
            filterArray.forEach(item => {
              const newItem = item.type === '=='
                ? [item.type, ['get', item.featureProperty], item.criteria.toUpperCase()]
                : [item.type, item.criteria.toUpperCase(), ['get', item.featureProperty]];
              filterInstructions.push(newItem);
            });
          } else {
            filterInstructions = null;
          }

          // now use the filterInstructions to filter each mapbox category layer of pins
          for (let i = 0, len = categoriesArray.length; i < len; i++) {
              map.setFilter(categoriesArray[i], filterInstructions, { validate: false });
          }
        };

        //////////////////////////////////////////////////////////////////////
        // function that FILTERS the mapbox DISTRICT layer based upon user
        // selection or deselection
        //////////////////////////////////////////////////////////////////////
        const highlightDistrict = districtVal => {
            // the filter key for districts is GEOID20
            // a GEOID20 is the State FIP numeral concatenated with the district numeral.
            // example: CA FIP is 06.
            // example: CA district #36 would be GEOID20 == '0636'
            // example: FL district #5 would be GEOID20 == '1205'

            const fip = this.stateToFIP(districtVal) || '';
            map.setFilter('district-highlighted', ['==', 'GEOID20', fip], { validate: false });
        };

        //////////////////////////////////////////////////////////////////////
        // function that SHOWS/HIDES mapbox category layers based upon user
        // selection or deselection/initial-page-load
        //////////////////////////////////////////////////////////////////////
        const filterCategory = category => {
            //remove any open FEATURED popups
            this.clearElems('.mapboxgl-popup');

            // remove any open map pin popups.
            ReactDOM.unmountComponentAtNode(this.tooltipClickContainer);

            // if category is '' then that is either initial page load or user has de-selected a category
            // so show all categories on the map
            for (let i = 0, len = categoriesArray.length; i < len; i++) {
                if (category === categoriesArray[i] || category === '') {
                    // address the SELECTED category or initial-load/deselection
                    map.setLayoutProperty(categoriesArray[i], 'visibility', 'visible');
                } else {
                    // address any categories NOT selected by the user - set them to none
                    map.setLayoutProperty(categoriesArray[i], 'visibility', 'none');
                }
            }
        };

        const flyAndZoom = (props = null, zoom = null) => {
            const centerVal = props || propCenter;
            const zoomVal = zoom || propZoom;
            return map.flyTo({
                center: centerVal,
                zoom: zoomVal,
                screenSpeed: 1, // 0.6,
                curve: 0.3,
                easing: t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
            });
        };

        // let { setDistrictCenter, congress } = this.props;
        const { congress } = this.props;
        const { uiState } = this.props;
        const { filter, selected, currentNav, searchType } = congress;
        const { abrvState, districtName, category, stateName } = selected;

        // const state = this.props.congress.selected.abrvState;
        // const number = this.props.congress.selected.district;
        const state = selected.abrvState;
        const number = selected.district;

        const { bounds, currentRequest, currentSearch, filterType, input } = uiState;

        let filterArray = [];

        // if there was a search performed prior to the filter, we add its criteria to the filterArray
        // if (searchType === 'keyword' && congress.orgs.length) {
        if (searchType === 'keyword') {
          filterArray.push({'featureProperty': 'name', 'criteria': currentRequest, 'type':'in' });
        }

        // console.log(this.props)
        if (filter === 'state') {

          const waiting = () => {
            const sourceLoaded = typeof map.getSource('tl-2023-us-cd118') != 'undefined';
            const layerLoaded = typeof map.getLayer('district-highlighted') != 'undefined';
            const readyToRun = map.isStyleLoaded() && sourceLoaded && layerLoaded;
            if (!readyToRun) {
                setTimeout(waiting, 300);
            } else {
              // user changed U.S. State select list
              if (abrvState !== 'US') {
                if (districtName === 'District') {
                    // no district selected
                    highlightDistrict(''); // clear any district polygon that may be showing from previous U.S. state selection

                    //  Since this is a state filter, we automatically add state criteria to our filterArray
                    filterArray.push({'featureProperty': 'state', 'criteria': abrvState, 'type': '=='});

                    showPins(filterArray).then(flyAndZoom(propCenter, propZoom));

                } else {
                    filterArray.push({'featureProperty': 'congress_district', 'criteria': state + number, 'type': '=='});

                    showPins(filterArray).then(flyAndZoom(propCenter, propZoom));
                }
                // flyAndZoom(propCenter, propZoom);
              } else {
                  // user DE-SELECTED a state, so show all
                  highlightDistrict(''); // clear any district polygon that may be showing from previous U.S. state selection
                  showPins(filterArray);
                  flyAndZoom([-105, 39], isMobile ? 2.2 : 3.5);
              }
            }
          }
          waiting();
        }

        if (filter === 'district') {

            const num = number.length === 1 ? 0 + number : number;
            const districtVal = state + num;
            const fips = this.stateToFIP(districtVal);
            const { zoom: usStateZoom, center: usStateCenter } = US_STATES_BOUNDS.find(
                usState => usState.state === stateName
            ).coordinates;

            if (number === 'US') {
                // user de-selected a district, since its value is US
                filterArray.push({'featureProperty': 'state', 'criteria': abrvState, 'type': '=='});

                await showPins(filterArray); // show all pins for this state
                highlightDistrict(''); // clear district polygon
                flyAndZoom(usStateCenter, usStateZoom);
            } else {
                filterArray.push({'featureProperty': 'congress_district', 'criteria': state + number, 'type': '=='});

                await showPins(filterArray);
                highlightDistrict(districtVal);

                if (dontReCenterOnMapDrag) return;

                // flyAndZoom(propCenter, null);

                const wait = async () => {
                    flyAndZoom(propCenter, usStateZoom);

                    const feature = map.querySourceFeatures('composite', {
                        sourceLayer: 'tl-2023-us-cd118-61x619',
                        filter: ['==', 'GEOID20', fips],
                    });

                    if (feature.length < 1) {
                        console.log('Pending Mapbox querySourceFeature.');
                        setTimeout(wait, 500);
                    } else {
                        const { INTPTLON20, INTPTLAT20 } = feature[0].properties;
                        flyAndZoom([parseFloat(INTPTLON20), parseFloat(INTPTLAT20)], propZoom);
                    }
                };
                await wait();
            }
        }

        if (filter === 'type') {

          const oldCategory = category === '' ? '' : category;
          const newCategory =
              oldCategory === '' ? oldCategory : `organizations-${oldCategory.replace(/ /g, '-').toLowerCase()}`;

          const waiting = () => {
            const sourceLoaded = typeof map.getSource('tl-2023-us-cd118') != 'undefined';
            const layerLoaded = typeof map.getLayer('district-highlighted') != 'undefined';
            const readyToRun = map.isStyleLoaded() && sourceLoaded && layerLoaded;
            if (!readyToRun) {
                setTimeout(waiting, 300);
            } else {

              if (abrvState !== 'US') {
                // U.S. State currently selected
                if (districtName === 'District') {
                    filterArray.push({'featureProperty': 'state', 'criteria': abrvState, 'type': '=='});
                    showPins(filterArray);
                } else {
                    // U.S. State and District currently selected
                    filterArray.push({'featureProperty': 'congress_district', 'criteria': state + number, 'type': '=='});
                    showPins(filterArray);
                }
              }

              filterCategory(newCategory);
            }
          };

          waiting();
        }

        if (filter === 'url') {

            const showAllDistricts = districtName === 'District';

            if (abrvState !== 'US' && window.location.search.indexOf('state') > -1) {
                // a U.S. state was passed into the URL
                if (showAllDistricts && window.location.search.indexOf('district') < 0) {
                    filterArray.push({'featureProperty': 'state', 'criteria': abrvState, 'type': '=='});
                    await showPins(filterArray);
                    const { zoom, center } = US_STATES_BOUNDS.find(usState => usState.state === stateName).coordinates;

                    if (!dontReCenterOnMapDrag) return;

                    flyAndZoom(center, zoom);
                } else {
                    // a district was also passed into the URL (along with state)

                    const num = number.length === 1 ? 0 + number : number;
                    const districtVal = state + num;
                    const fips = this.stateToFIP(districtVal);
					// eslint-disable-next-line prefer-const
                    let { zoom, center } = US_STATES_BOUNDS.find(usState => usState.state === stateName).coordinates;

                    const waitOnDistrictShadingLayer = () => {
                        // have to wait on mapbox district source and shading layer to exist prior to filtering on it.
                        const layerLoaded = typeof map.getLayer('district-highlighted') != 'undefined';

                        if (!layerLoaded) {
                            // mapbox district source and shading layer not yet loaded, so wait again
                            setTimeout(waitOnDistrictShadingLayer, 300);
                        } else {
                            // mapbox district source and shading layer exists, so can filter on pins and shaded layer
                            filterArray.push({'featureProperty': 'congress_district', 'criteria': state + number, 'type': '=='});
                            showPins(filterArray);
                            highlightDistrict(districtVal);
                        }
                    };
                    waitOnDistrictShadingLayer();

                    if (!dontReCenterOnMapDrag) {
                        await flyAndZoom(center, zoom);
                    }

                    const wait = () => {
                        const feature = map.querySourceFeatures('composite', {
                            sourceLayer: 'tl-2023-us-cd118-61x619',
                            filter: ['==', 'GEOID20', fips],
                        });

                        if (feature.length === 0) {
                            setTimeout(wait, 300);
                        } else {
                            if (dontReCenterOnMapDrag) return;

                            if (document.getElementsByClassName('has-district-zoom').length) {
                                zoom = document
                                    .getElementsByClassName('has-district-zoom')[0]
                                    .getAttribute('data-district-zoom');
                            }

                            const { INTPTLON20, INTPTLAT20 } = feature[0].properties;

                            flyAndZoom([parseFloat(INTPTLON20), parseFloat(INTPTLAT20)], zoom);
                        }
                    };
                    wait();
                }
            }

            if (window.location.search.indexOf('category') > -1) {
                // only need to run this if category is in the URL
                const oldCategory = category === '' ? '' : category;
                const newCategory =
                    oldCategory === '' ? oldCategory : `organizations-${  oldCategory.replace(/ /g, '-').toLowerCase()}`;

                const waiting = () => {
                    const sourceLoaded = typeof map.getSource('tl-2023-us-cd118') != 'undefined';
                    const layerLoaded = typeof map.getLayer('district-highlighted') != 'undefined';

                    const readyToRun = styleLoaded && sourceLoaded && layerLoaded;
                    if (!readyToRun) {
                        setTimeout(waiting, 300);
                    } else {
                        if (newCategory !== '') {
                            filterCategory(newCategory);
                        }
                    }
                };
                waiting();
            }
        }

        if (filter === 'search' && searchType !=='') {

          const waiting = () => {
              const sourceLoaded = typeof map.getSource('tl-2023-us-cd118') != 'undefined';
              const layerLoaded = typeof map.getLayer('district-highlighted') != 'undefined';
              const readyToRun = styleLoaded && sourceLoaded && layerLoaded;
              // console.log(!{ readyToRun });
              if (!readyToRun) {
                  setTimeout(waiting, 300);
              } else {

                // any text search should clear the district shading
                // highlightDistrict('');

                if (congress.orgs.length >= 0) {
                  switch (searchType) {
                    case 'keyword':

                      // clear stars from map
                      this.clearElems('.star.mapboxgl-marker');

                      if (congress.orgs.length > 0 || filterType === 'keyword') {
                        let stateAbbrev = false, keywords = '';
                        // let textInputArray = currentRequest.split(/[ ]+/); // parse on space
                        const textInputArray = currentSearch.split(/[ ]+/); // parse on space

                        // separate the U.S.State term from the text input
                        textInputArray.forEach(term => {
                          const stateFound = US_STATES_BOUNDS.find(usState => (usState.abrv.toLowerCase() === term || usState.state.toLowerCase() === term));
                          stateFound ? stateAbbrev = stateFound.abrv : keywords += ` ${  term}`;
                        });

                        this.showFeaturedOrgIcons();

                        filterArray.push({'featureProperty': 'name', 'criteria': keywords.trim(), 'type': 'in'});

                        if (abrvState !== 'US') {
                          // U.S. State currently selected
                          if (districtName === 'District') {
                              filterArray.push({'featureProperty': 'state', 'criteria': abrvState, 'type': '=='});
                          } else {
                              // U.S. State and District currently selected
                              filterArray.push({'featureProperty': 'congress_district', 'criteria': state + number, 'type': '=='});
                          }
                        }
                      }

                      // if no results and was valid location then show all pins,
                      // otherwise (invalid location or invalid zip) show no pins
                      if (congress.orgs.length === 0) {
                        input.validLocation === true
                          ? filterArray = []
                          : filterArray = [{'featureProperty': 'state', 'criteria': 'ZZ', 'type': '=='}]; // use dummy state (ZZ) to filter out ALL pins
                      }

                      showPins(filterArray);
                      // ACTION ITEM: Still need to code in the logic to handle the U.S. state filtering when coupled with a keyword
                      // ex: user enters 'habitat fl' or 'habitat, fl'
                      // it needs to get orgs containing habitat in the name, and that exist in Florida
                      // so need to enhance showPins function to take in multiple criteria at once
                      break;

                    case 'region':

                      // clear stars from map
                      this.clearElems('.star.mapboxgl-marker');

						// eslint-disable-next-line no-case-declarations
                      const stateFound = US_STATES_BOUNDS.find(usState => (
                        usState.abrv.toLowerCase() === currentRequest.toLowerCase() ||
                        usState.state.toLowerCase() === currentRequest.toLowerCase()
                      ));
                      stateFound &&
                        (filterArray.push({'featureProperty': 'state', 'criteria': stateFound.abrv, 'type': '=='}),
                          showPins(filterArray),
                        this.showFeaturedOrgIcons());
                        // .then(flyAndZoom(propCenter, propZoom))
                      break;

                    case 'place':
                    case 'postcode':
                      showPins([]);
                      break;

                    default:

                  }

                  if (filterType === 'location' && congress.orgs.length === 0 && input.validLocation === true) {
                    // no results and was a valid location based search
                    // so zoom to a certain level and center on that location
                    map.setZoom(7.5);
                    map.panTo(input.center);
                  } else {
                    if (abrvState === 'US' && districtName === 'District') {
                      // only apply map bounds when state and district were NOT selected
                      map.fitBounds(
                        [
                            [bounds.minLong, bounds.minLat],
                            [bounds.maxLong, bounds.maxLat],
                        ],
                        {
                            padding: {top: 25, bottom:25, left: 10, right: 10},
                            linear: true,
                            maxZoom: 13,
                        },
                      );
                    }
                  }
                }
              }
          };

          waiting();
      }


      if (filter === '' && currentNav === 'default' && mapOnLoad === true) {
          const waiting = () => {
              const sourceLoaded = typeof map.getSource('tl-2023-us-cd118') != 'undefined';
              const layerLoaded = typeof map.getLayer('district-highlighted') != 'undefined';
              const readyToRun = styleLoaded && sourceLoaded && layerLoaded;
              // console.log(!{ readyToRun });
              if (!readyToRun) {
                  setTimeout(waiting, 300);
              } else {
                  highlightDistrict('');
                  showPins([]);
                  filterCategory('');
                  flyAndZoom();
              }
          };
          waiting();
      }
    }

    componentDidMount() {
        const {
            requestType,
            handleEvent,
            location,
            isNearbySearch,
            currentRequest,
            bounds,
            totalResults,
            searchResultsByCategory,
            initialFeaturesLoaded,
        } = this.props;

        this.props.onRef(this);

        map = new mapboxgl.Map({
            container: this.mapContainer,
            style: fullMap,
            // zoom: this.props.congress.mapbox.zoom,
            // rotation: this.props.congress.mapbox.rotation,
            // center: this.props.congress.mapbox.center,
            zoom: 3.5,
            rotation: -10,
            // center: this.props.congress.mapbox.center,
            center: window.innerWidth < 768 ? [-98, 32] : [-105, 39],
        });

        const zoomBtns = new mapboxgl.NavigationControl({
            showCompass: false,
        });
        map.addControl(zoomBtns, isMobile ? 'top-right' : 'bottom-right');

        map.on('load', () => {
            document.getElementsByClassName('mapboxgl-map')[0].id = 'TheCDMap';
        });

        map.on('click', e => {
            // if the click event did NOT hit the map canvas, then it must have hit
            // the star or numbered balloon on the map. So don't show any popups from the
            // map pins.
            if (!e.originalEvent.target.classList.contains('mapboxgl-canvas')) {
                // since we did not click on the map canvas pin, we need to close any existing
                // mapbox popups that might by open.
                const mapboxMarker = document.getElementById('cd-mapbox-pin');
                if (mapboxMarker) {
                    ReactDOM.unmountComponentAtNode(this.tooltipClickContainer);
                }

                // This prevents duplicate popups from being displayed when clicking on a
                // balloon or star in a cluster of orgs.
                return;
            }

            const features = map.queryRenderedFeatures(e.point);

            // remove features that arent organizations (only get features with a property id)
            const orgs = features.filter(org => org.properties.id);

            if (orgs !== [] && orgs !== undefined) {
                if (orgs.length > 0 && orgs[0].properties.id) {
                    if (orgs[0].properties.is_featured !== '1') {
                        fireEvents(orgs[0].properties.name);
                    }

                    // this is for PINS taps/touches not Balloons or Stars
                    isMobile
                        ? map.panTo(e.lngLat, { animate: true, offset: [0, 80] })
                        : map.panBy(getPanDistance(e.point, false), { animate: true });
                }
            }

            // create the popup using the div container
            const pinPopup = new mapboxgl.Marker(this.tooltipClickContainer, {
                anchor: 'bottom',
            });

            // set the popup's location to the click point on the map
            pinPopup.setLngLat(e.lngLat);

            // add the pop on the map
            pinPopup.addTo(map);

            // show the popup on the map
            this.setTooltip(orgs);
        });

        map.on('style.load', () => {
            const waiting = () => {
                if (!map.isStyleLoaded()) {
                    setTimeout(waiting, 200);
                } else {
                    styleLoaded = true;
                    // Add the source to query. In this example we're using
                    // congress district polygons uploaded as vector tiles
                    map.addSource('tl-2023-us-cd118', {
                        type: 'vector',
                        url: 'mapbox://jacobhanson.dkol8f97',
                    });
                    map.addLayer(
                        {
                            id: 'district-highlighted',
                            type: 'fill',
                            source: 'tl-2023-us-cd118',
                            'source-layer': 'tl-2023-us-cd118-61x619',
                            paint: {
                                'fill-outline-color': '#84888a',
                                'fill-color': '#d6d7d6',
                                'fill-opacity': 0.5,
                            },
                            filter: ['in', 'GEOID20', ''], //GEOID20 is the filter key for districts
                        },
                        'state-label-lg'
                    ); // Place polygon under these labels.

                    // This listener is to handle the HOVER behavior on the map
                    map.on('mousemove', (e) => {
                        // this shows the hover poppup on a district
                        // Change the cursor style as a UI indicator.
                        const features = map.queryRenderedFeatures(e.point);
                        // remove features that arent organizations (only get features with a property id)
                        const orgs = features.filter(org => org.properties.id);
                        map.getCanvas().style.cursor = orgs.length ? 'pointer' : 'grab';
                    });
                }
            };
            waiting();
        });

        map.once('load', () => {
            mapOnLoad = true;
        });

        this.updateMarkers();
        this.setIt();
    }

    getZoom = () => map.getZoom();

    getCenter = () => map.getCenter();

    clearElems = (elem) => {
      // remove any stars or balloon from map if they exist
      if (document.querySelectorAll(elem).length) {
        Array.prototype.slice.call(document.querySelectorAll(elem), 0).forEach(e => {e.parentNode.removeChild(e);
        });
      }
    }

    componentDidUpdate(prevProps) {
        const { searchResults, initialFeaturesLoaded, congress } = this.props;

        const testResults = JSON.stringify(searchResults) !== JSON.stringify(prevProps.searchResults);
        const testInitialFeaturesLoaded = initialFeaturesLoaded !== prevProps.initialFeaturesLoaded;

        // You cannot compare two objects directly. JSON.stringify first to quickly compare values, not methods.
        if (testResults || testInitialFeaturesLoaded) {
            this.updateMarkers();
        }

        // when you drag the map, it updates the state
        // normally on state change, the map updates to center on the selected district
        // this is fine if you select a new district from the dropdown, but not if the district hasn't changed.
        // to allow the user to drag the map and not have it reset, we need this condition
        const dontReCenterOnMapDrag = prevProps.congress.selected.districtName === congress.selected.districtName;

        this.setIt(dontReCenterOnMapDrag);
    }

    componentWillUnmount() {
        this.props.onRef(undefined);
        map.remove();
    }

    showFeaturedOrgIcons() {

        const { categoryId } = this.props.congress.selected;
        const { abrvState, district } = this.props.congress.selected;
        const {searchType} = this.props.congress;
        const { currentRequest } = this.props.uiState;
        const { orgs } = this.props.congress;

        let categoryValueType = categoryId === '' ? 'all' : categoryId;

        // adjust our DB category value to numeral since location based searches come from Mapbox and
        // have category ID as numeral whereas our DB has it has string
        if (categoryValueType !== 'all') {
          categoryValueType = searchType === 'postcode' || searchType === 'place'
          ? +categoryId
          : categoryId;
        }

        const markers = this.state.markers || [];

        // clear stars from map
        this.clearElems('.star.mapboxgl-marker');

        if (orgs) {
            // eslint-disable-next-line no-loop-func
            orgs.forEach((star, index) => {
                if (star.properties.is_featured === '1') {
                    const el = document.createElement('div');
                    el.className = `star star${star.properties.id}`;
                    el.addEventListener('click', evt => {
                        fireEvents(star.properties.name);

                        const starLngLat = { lng: star.geometry.coordinates[0], lat: star.geometry.coordinates[1] };

                        isMobile
                            ? map.panTo(starLngLat, { animate: true, offset: [0, 160] })
                            : map.panBy(getPanDistance(evt, star.properties.is_featured), { animate: true });
                    });

                    el.innerHTML = `<strong class="star ${
                        COLORS[star.properties.category_id]
                    }-fg">&#9733;</strong>`;

                    // this is the popup object.
                    const thisStarPopup = new mapboxgl.Popup({
                        offset: [0, 0],
                        anchor: 'bottom',
                        maxWidth: '270px',
                    });

                    // eslint-disable-next-line no-useless-concat
                    thisStarPopup.setHTML(
                        `${'<article class="organizations__popup featured markerpopup isPopup"><a class="isPopup" href="' +
                            `${process.env.PUBLIC_URL}` +
                            '/organization/?search='}${star.properties.id}"><span class="category ${
                            COLORS[star.properties.category_id]
                        } isPopup">${
                            COMMUNITIES_TO_NAMES[IDS_TO_COMMUNITIES[star.properties.category_id]]
                        }</span><div class="img isPopup"><img src="` +
                            `${process.env.PUBLIC_URL}` +
                            `/images/org/featured/${star.properties.id}.jpg" title="Featured Organization: ${star.properties.name}" alt="Featured Organization: ${star.properties.name}" class="featured-image isPopup" /></div><div class="copy isPopup"><h3><small>Featured Organization</small><span class="isPopup">${star.properties.name}</span></h3><address class="isPopup">${star.properties.city}, ${star.properties.state}</address><p class="text-cta isPopup">See the Impact</p></div></a></article>`
                    );

                    // this is the star icon on the map
                    const thisStar = new mapboxgl.Marker({
                        element: el,
                        offset: [2, -20],
                    })
                        .setLngLat(star.geometry.coordinates)
                        .setPopup(thisStarPopup);

                    if (document.querySelectorAll(`.star${star.properties.id}`).length === 0) {
                        if (categoryValueType !== 'all') {
                            // user has selected a category
                            if (star.properties.category_id === categoryValueType) {
                                // featured org category matches user selected category
                                if (district !== 'US') {
                                    // user has selected a district, and ultimately a state, since district is dependant on state
                                    if (star.properties.congress_district.substring(2) === district && star.properties.state === abrvState) {
                                        // featured org district and state matched user selection
                                        if (searchType === 'keyword') {
                                          // had a keyword serch, so need to make sure that matches
                                          if (star.properties.name.indexOf(currentRequest.toUpperCase()) > -1) {
                                            // featured org name contained keyword(s)
                                            thisStar.addTo(map);
                                            markers.push(thisStar);
                                          }
                                        } else {
                                          thisStar.addTo(map);
                                          markers.push(thisStar);
                                        }
                                    }
                                } else {
                                    // no district selected
                                    if (abrvState !== 'US') {
                                        // but state was selected
                                        if (star.properties.state === abrvState) {
                                            // featured org state matched user selection
                                            if (searchType === 'keyword') {
                                              // had a keyword serch, so need to make sure that matches
                                              if (star.properties.name.indexOf(currentRequest.toUpperCase()) > -1) {
                                                // featured org name contained keyword(s)
                                                thisStar.addTo(map);
                                                markers.push(thisStar);
                                              }
                                            } else {
                                              thisStar.addTo(map);
                                              markers.push(thisStar);
                                            }
                                        }
                                    } else {
                                        // state not selected
                                        if (searchType === 'keyword') {
                                          // had a keyword serch, so need to make sure that matches
                                          if (star.properties.name.indexOf(currentRequest.toUpperCase()) > -1) {
                                            // featured org name contained keyword(s)
                                            thisStar.addTo(map);
                                            markers.push(thisStar);
                                          }
                                        } else {
                                          thisStar.addTo(map);
                                          markers.push(thisStar);
                                        }
                                    }
                                }
                            }
                        } else {
                          // category not selected
                          if (district !== 'US') {
                            // user has selected a district, and ultimately a state, since district is dependant on state
                            if (star.properties.congress_district.substring(2) === district && star.properties.state === abrvState) {
                              // featured org district and state matched user selection
                              if (searchType === 'keyword') {
                                // had a keyword serch, so need to make sure that matches
                                if (star.properties.name.indexOf(currentRequest.toUpperCase()) > -1) {
                                  // featured org name contained keyword(s)
                                  thisStar.addTo(map);
                                  markers.push(thisStar);
                                }
                              } else {
                                thisStar.addTo(map);
                                markers.push(thisStar);
                              }
                            }
                          } else {
                            // no district selected
                            if (abrvState !== 'US') {
                              // but state was selected
                              if (star.properties.state === abrvState) {
                                  // featured org state matched user selection
                                  if (searchType === 'keyword') {
                                    // had a keyword serch, so need to make sure that matches
                                    if (star.properties.name.indexOf(currentRequest.toUpperCase()) > -1) {
                                      // featured org name contained keyword(s)
                                      thisStar.addTo(map);
                                      markers.push(thisStar);
                                    }
                                  } else {
                                    thisStar.addTo(map);
                                    markers.push(thisStar);
                                  }
                              }
                            } else {
                              // state not selected
                              if (searchType === 'keyword') {
                                // had a keyword serch, so need to make sure that matches
                                if (star.properties.name.indexOf(currentRequest.toUpperCase()) > -1) {
                                  // featured org name contained keyword(s)
                                  thisStar.addTo(map);
                                  markers.push(thisStar);
                                }
                              } else {
                                thisStar.addTo(map);
                                markers.push(thisStar);
                              }
                            }
                          }
                        }
                    }
                }
            });
        } else {
            return;
        }

        if (document.querySelectorAll('.star').length) {
            Array.prototype.slice.call(document.querySelectorAll('.star'), 0).forEach((star, i) => {
                star.onclick = function(e) {
                    // remove additional click popups
                    setTimeout(() => {
                        if (document.querySelectorAll('.click').length) {
                            Array.prototype.slice
                                .call(document.querySelectorAll('.click'), 0)
                                // eslint-disable-next-line no-shadow
                                .forEach(e => e.parentNode.removeChild(e));
                        }
                    }, 300);
                    // reset z-index for all stars, use 'null' to fallback to css values
                    if (document.querySelectorAll('.marker').length) {
                        Array.prototype.slice.call(document.querySelectorAll('.marker'), 0).forEach(marker => {
                            marker.style.zIndex = null;
                        });
                    }
                    if (document.querySelectorAll('.star.mapboxgl-marker').length) {
                        Array.prototype.slice
                            .call(document.querySelectorAll('.star.mapboxgl-marker'), 0)
                            // eslint-disable-next-line no-shadow
                            .forEach((star, i) => {
                                star.style.zIndex = null;
                            });
                    }
                    e.target.parentNode.style.zIndex = 2;
                };
            });
        }
    }

    //////////////////////////////////////////////////////////////////////
    // function when map CHANGES
    //////////////////////////////////////////////////////////////////////
    updateMarkers() {

        if (this.props.congress.filter === 'url-loading') return;

        // remove existing markers
        this.clearElems('.marker');

        const markers = this.state.markers || [];

        this.props.searchResults.forEach((marker, index) => {
            let markerNumber;

            // Get marker number
            if (this.props.currentPage === 1) {
                markerNumber = index + 1;
            } else if (this.props.currentPage > 1) {
                markerNumber =
                    index === 9 ? `${this.props.currentPage}${0}` : `${this.props.currentPage - 1}${index + 1}`;
            }

            //Set Marker HTML
            const el = document.createElement('div');
            const digits = `${markerNumber}`.length;
            el.className = 'marker';
            el.className += ` balloon-${marker.properties.category_id}`; // add balloon CSS class
            el.className += digits <= 2 ? '' : digits <= 3 ? ' narrow' : digits <= 4 ? ' wide' : ' super-wide'; // add balloon width CSS class
            el.innerHTML = `<strong style="color: ${
                CONGRESS_COLORS[marker.properties.category_id].font_color
            }">${markerNumber}</strong>`;
            el.addEventListener('click', (e) => {
                fireEvents(marker.properties.name);
                const balloonLngLat = { lng: marker.geometry.coordinates[0], lat: marker.geometry.coordinates[1] };
                const offSetY = marker.properties.is_featured === '1' ? 160 : 80; // balloons can be featured or non-featured, so height is different
                isMobile
                    ? map.panTo(balloonLngLat, { animate: true, offset: [0, offSetY] })
                    : map.panBy(getPanDistance(e, marker.properties.is_featured), { animate: true });
            });

            //Set PopUp HTML - this is the popup container
            this.thisMarkerPopup = new mapboxgl.Popup({
                offset: [0, 0],
                anchor: 'bottom',
                maxWidth: '270px',
            });

            if (marker.properties.is_featured === '1') {
                this.thisMarkerPopup.setHTML(
                    `'<article class="organizations__popup featured markerpopup isPopup"><span class="category ${
                        COLORS[marker.properties.category_id]
                    } isPopup">${
                        COMMUNITIES_TO_NAMES[IDS_TO_COMMUNITIES[marker.properties.category_id]]
                    }</span><div class="img isPopup"><img src="` +
                        `${process.env.PUBLIC_URL}` +
                        `/images/org/featured/${marker.properties.id}.jpg" title="Featured Organization: ${marker.properties.name}" alt="Featured Organization: ${marker.properties.name}" class="featured-image isPopup" /></div><div class="copy isPopup"><h3 class="isPopup"><small class="isPopup">Featured Organization</small><span class="isPopup">${marker.properties.name}</span></h3><address class="isPopup">${marker.properties.city}, ${marker.properties.state}</address></article>`
                );
            } else {
                // eslint-disable-next-line no-useless-concat
                this.thisMarkerPopup.setHTML(
                    `<article class="organizations__popup markerpopup isPopup"><span class="category ${
                        COLORS[marker.properties.category_id]
                    } isPopup">${
                        COMMUNITIES_TO_NAMES[IDS_TO_COMMUNITIES[marker.properties.category_id]]
                    }</span><div class="copy isPopup"><h3 class="isPopup"><span class="isPopup">${
                        marker.properties.name
                    }</span></h3><address class="isPopup">${marker.properties.city}, ${
                        marker.properties.state
                    }</address></div></article>`
                );
            }

            const yOffset = digits <= 3 ? -15 : -20;
            //Create Mapbox Marker and Popup from HTML - this is for the balloons
            const thisMarker = new mapboxgl.Marker({
                element: el,
                offset: [0, yOffset],
            })
                .setLngLat(marker.geometry.coordinates)
                .setPopup(this.thisMarkerPopup)
                .addTo(map);

            // Push Markers to Array
            markers.push(thisMarker);
        });

        // TEST vigorously to see if this is needed.
        // this.setState({markers: markers}, () => {
        // });

        if (document.querySelectorAll('.marker').length) {
            Array.prototype.slice.call(document.querySelectorAll('.marker'), 0).forEach((marker, i) => {
                marker.onclick = e => {
                    // remove additional click popups
                    setTimeout(() => {
                        if (document.querySelectorAll('.click').length) {
                            Array.prototype.slice
                                .call(document.querySelectorAll('.click'), 0)
                                // eslint-disable-next-line no-shadow
                                .forEach(e => e.parentNode.removeChild(e));
                        }
                    }, 300);
                    // reset z-index for all markers, use 'null' to fallback to css values
                    if (document.querySelectorAll('.marker').length) {
                        // eslint-disable-next-line no-shadow
                        Array.prototype.slice.call(document.querySelectorAll('.marker'), 0).forEach((marker, i) => {
                            marker.style.zIndex = null;
                        });
                    }
                    if (document.querySelectorAll('.star.mapboxgl-marker').length) {
                        Array.prototype.slice
                            .call(document.querySelectorAll('.star.mapboxgl-marker'), 0)
                            // eslint-disable-next-line no-shadow
                            .forEach((star, i) => {
                                star.style.zIndex = null;
                            });
                    }
                    e.target.parentNode.style.zIndex = 4;
                };
            });
        }

        this.showFeaturedOrgIcons();
    }

    setTooltip(features) {
        const container = this.tooltipClickContainer;

        if (features.length) {
            // limit to the first featured org, or first org if no featured in set.
            const newFeatures = [];
            features.forEach(feature => {
                if (feature.properties.is_featured === '1') {
                    newFeatures.push(feature);
                }
            });

            const tooltipFeatures = newFeatures.length ? newFeatures[0] : features[0];

            ReactDOM.render(
                React.createElement(Tooltip, {
                    features: tooltipFeatures,
                }),
                container
            );
        } else {
            ReactDOM.unmountComponentAtNode(this.tooltipClickContainer);
        }
    }

    render() {
        // defaults (these are for desktop)
        // top accounts for district filters
        // height is entirety of parent less top nav and district filters
        // width is entirety of parent less right rail category filters
        let mapTop = '50px',
            // mapHeight = 'calc(100vh - 110px)',
            // mapHeight = 'calc(100vh)',
            mapHeight = 'calc(100vh - 60px)',
            mapWidth = 'calc(100% - 150px)';

        // adjust accordingly for mobile
        // since can't resize browsers on mobile, have to reload browser to see correct layouts
        // 287px = 80px bottom list results + 42px top red nav + 55px search input + 55px category/state filter + 55px district filter

        if (isMobile) {
            mapWidth = '100%';
            mapTop = '0px';
            mapHeight = `${window.innerHeight - 287  }px`;

            if (isMobileWide) {
                mapTop = '0px';
                mapHeight = `${window.innerHeight - 300  }px`;
            }
        }

        const style = {
            position: 'relative',
            top: mapTop,
            height: mapHeight,
            width: mapWidth,
        };

        // eslint-disable-next-line no-return-assign
        return <div style={style} ref={el => (this.mapContainer = el)} />;
    }
}

export default CongressMap;
